import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useQuery } from "react-query";
import useLang from "Shared/hooks/useLanguage";
import { sortById, SortingTypeI } from "Utils/consts";
import { Loader } from "Shared/Common/Loader";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import usePagination from "Shared/hooks/usePagination";
import { InstrumentGetAll } from "Services/Instrument/Instrument";
import NoRecord from "Shared/Common/NoRecord";
import ActiveInstrumentRow from "./row";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";

const initialSearchCriteria = {
  instrumentName: "",
  instrumentDescription: "",
  instrumentType: "",
};

const initialPostData = {
  instrumentId: 0,
  instrumentName: "",
  instrumentDescription: "",
  instrumentType: "",
};

const ActiveInstruments: React.FC = () => {
  const { t } = useLang();
  const [triggerSearchData, setTriggerSearchData] = useState(false);
  const [sortTrigger, setSortTrigger] = useState(false);
  const [localRows, setLocalRows] = useState<any[]>([]);
  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(initialSearchCriteria);
  const [reset, setReset] = useState(false);
  const [sort, setSorting] = useState<SortingTypeI>(sortById);

  const {
    total,
    curPage,
    showPage,
    nextPage,
    prevPage,
    setTotal,
    pageSize,
    totalPages,
    pageNumbers,
    setPageSize,
    setCurPage,
  } = usePagination();

  const handleSort = (columnName: any) => {
    const newSortOrder =
      sort.clickedIconData === columnName
        ? sort.sortingOrder === "asc"
          ? "desc"
          : "asc"
        : "asc";
    setSorting({ sortingOrder: newSortOrder, clickedIconData: columnName });
    setSortTrigger((prev) => !prev);
  };

  const queryPayload = {
    pageNumber: curPage,
    pageSize: pageSize,
    queryModel: {
      instrumentName: searchCriteria.instrumentName || "",
      instrumentDescription: searchCriteria.instrumentDescription || "",
      instrumentType: searchCriteria.instrumentType || "",
      isActive: true,
    },
    sortColumn: sort.clickedIconData || "Id",
    sortDirection: sort.sortingOrder || "Desc",
  };

  const {
    data: queryResult,
    isFetching: loading,
    refetch,
  } = useQuery(
    [
      "instruments-active",
      curPage,
      pageSize,
      triggerSearchData,
      sortTrigger,
      reset,
    ],
    () => InstrumentGetAll(queryPayload),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: (resp: any) => {
        setTotal(resp?.data?.total ?? 0);

      },
      onError: (error: any) => {
        console.error(t("Error fetching instrument data:"), error);
      },
    }
  );

  const apiRows: any[] = queryResult?.data?.data ?? [];
  const localRowIds = new Set(localRows.map((r) => r.instrumentId));
  const filteredApiRows = apiRows.filter((r) => !localRowIds.has(r.instrumentId));
  const tableData = [...localRows, ...filteredApiRows];

  const handleReset = () => {
    setSearchCriteria(initialSearchCriteria);
    setSorting(sortById);
    setCurPage(1);
    setReset((prev) => !prev);
  };

  const handleSearch = () => {
    setCurPage(1);
    setTriggerSearchData((prev) => !prev);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") handleSearch();
  };

  return (
    <>
      <div className="d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center responsive-flexed-actions mb-2 gap-2">
        <div className="d-flex align-items-center responsive-flexed-actions gap-2">
          <div className="d-flex align-items-center">
            <span className="fw-400 mr-2">{t("Records")}</span>
            <select
              id="InstrumentActiveRecords"
              className="form-select w-100px h-33px rounded py-2"
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurPage(1);
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <PermissionComponent
            moduleName="Oncology LIS"
            pageName="Instruments"
            permissionIdentifier="AddNew"
          >
            {({ pageName, moduleName, permissionIdentifier }) => (
              <button
                id="InstrumentActiveAddNew"
                className="btn btn-primary btn-sm btn-primary--icon px-7"
                onClick={() => {
                  setLocalRows((prev) => [
                    { rowStatus: true, ...initialPostData },
                    ...prev,
                  ]);
                }}
                disabled={loading}
              >
                <i className="fa-solid fa-plus"></i>
                <span style={{ fontSize: 11 }}>
                  {getPermissionDisplayName(
                    pageName,
                    moduleName,
                    permissionIdentifier,
                    "Create Instrument"
                  )}
                </span>
              </button>
            )}
          </PermissionComponent>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            id="InstrumentActiveSearch"
            className="btn btn-linkedin btn-sm fw-500"
            onClick={handleSearch}
          >
            {t("Search")}
          </button>
          <button
            id="InstrumentActiveReset"
            className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
            type="button"
            onClick={handleReset}
          >
            <span>{t("Reset")}</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <Box sx={{ height: "auto", width: "100%" }}>
          <div className="table_bordered overflow-hidden">
            <TableContainer
              sx={{
                maxHeight: "calc(100vh - 100px)",
                "&::-webkit-scrollbar": { width: 7 },
                "&::-webkit-scrollbar-track": { backgroundColor: "#fff" },
                "&:hover": {
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "var(--kt-gray-400)",
                    borderRadius: 2,
                  },
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--kt-gray-400)",
                  borderRadius: 2,
                },
              }}
              component={Paper}
              className="shadow-none"
            >
              <Table
                aria-label="instrument active table"
                className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                <TableHead>
                  {/* Search row */}
                  <TableRow className="h-40px">
                    <TableCell className="w-50px" />
                    <TableCell style={{ minWidth: "180px" }}>
                      <input
                        type="text"
                        name="instrumentName"
                        className="form-control bg-white min-w-150px w-100 rounded-2 fs-8 h-30px"
                        placeholder="Search..."
                        value={searchCriteria.instrumentName}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          setSearchCriteria((prev) => ({
                            ...prev,
                            instrumentName: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "220px" }}>
                      <input
                        type="text"
                        name="instrumentDescription"
                        className="form-control bg-white min-w-150px w-100 rounded-2 fs-8 h-30px"
                        placeholder="Search..."
                        value={searchCriteria.instrumentDescription}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          setSearchCriteria((prev) => ({
                            ...prev,
                            instrumentDescription: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "180px" }}>
                      <input
                        type="text"
                        name="instrumentType"
                        className="form-control bg-white min-w-150px w-100 rounded-2 fs-8 h-30px"
                        placeholder="Search..."
                        value={searchCriteria.instrumentType}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          setSearchCriteria((prev) => ({
                            ...prev,
                            instrumentType: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                  </TableRow>

                  {/* Column headers row */}
                  <TableRow className="h-30px">
                    <TableCell>{t("Actions")}</TableCell>
                    <TableCell style={{ minWidth: "180px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("instrumentName")}
                      >
                        <div style={{ width: "max-content" }}>
                          {t("Instrument Name")}
                        </div>
                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "instrumentName"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "instrumentName"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "220px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("instrumentDescription")}
                      >
                        <div style={{ width: "max-content" }}>
                          {t("Catalog Number")}
                        </div>
                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "instrumentDescription"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "instrumentDescription"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "180px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("instrumentType")}
                      >
                        <div style={{ width: "max-content" }}>
                          {t("Serial Number")}
                        </div>
                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "instrumentType"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "instrumentType"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : tableData?.length ? (
                    tableData.map((row: any, index: number) => (
                      <ActiveInstrumentRow
                        key={row.instrumentId ?? `new-${index}`}
                        row={row}
                        index={index}
                        setLocalRows={setLocalRows}
                        refetch={refetch}
                        setIsBtnDisabled={setIsBtnDisabled}
                      />
                    ))
                  ) : (
                    <NoRecord colSpan={4} />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </div>

      {/* Pagination */}
      <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mt-4">
        <p className="pagination-total-record mb-0">
          <span>
            {t("Showing")} {pageSize * (curPage - 1) + 1} {t("to")}{" "}
            {Math.min(pageSize * curPage, total)} {t("of Total")}
            <span> {total} </span> {t("entries")}
          </span>
        </p>
        <ul className="d-flex align-items-center justify-content-end custome-pagination p-0 mb-0">
          <li className="btn btn-lg p-2 h-33px" onClick={() => showPage(1)}>
            <i className="fa fa-angle-double-left"></i>
          </li>
          <li className="btn btn-lg p-2 h-33px" onClick={prevPage}>
            <i className="fa fa-angle-left"></i>
          </li>
          {pageNumbers.map((page) => (
            <li
              key={page}
              className={`px-2 ${
                page === curPage
                  ? "font-weight-bold bg-primary text-white h-33px"
                  : ""
              }`}
              style={{ cursor: "pointer" }}
              onClick={() => showPage(page)}
            >
              {page}
            </li>
          ))}
          <li className="btn btn-lg p-2 h-33px" onClick={nextPage}>
            <i className="fa fa-angle-right"></i>
          </li>
          <li
            className="btn btn-lg p-2 h-33px"
            onClick={() => showPage(totalPages === 0 ? curPage : totalPages)}
          >
            <i className="fa fa-angle-double-right"></i>
          </li>
        </ul>
      </div>
    </>
  );
};

export default ActiveInstruments;
